/** @jsxImportSource @emotion/react */
import React from 'react';
import { DatePicker, Form, Input, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { datePickerFormat } from '../../../../i18n';
import commonStyles from '../../../../styles/commonStyles';
import { resolveEditableEventDateLabel } from '../utils';
import type { PlantingEvent } from '../../../../types/plantingEvents';
import type { FormInstance } from 'antd';
import dayjs from 'dayjs';
import { disableBeforeOpenDay } from '../../../../utils/openDayDate';

interface Props {
  open: boolean;
  editingEvent: PlantingEvent | null;
  form: FormInstance<{ eventDate: dayjs.Dayjs | null; notes: string }>;
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
  /** Event lies in the computed past: date is read-only, the note stays editable. */
  dateLocked?: boolean;
  /** Open day (D+1): the earliest date an event can be moved to. */
  openDay?: string | null;
}

const EditEventModal: React.FC<Props> = ({
  open,
  editingEvent,
  form,
  saving,
  onSave,
  onCancel,
  dateLocked = false,
  openDay = null,
}) => {
  const { t } = useTranslation('plantings');
  return (
  <Modal
    title={t('editEvent.title')}
    open={open}
    onCancel={onCancel}
    onOk={onSave}
    okText={t('common:actions.save')}
    cancelText={t('common:actions.cancel')}
    confirmLoading={saving}
  >
    <Form form={form} layout="vertical">
      <Form.Item
        label={resolveEditableEventDateLabel(editingEvent)}
        name="eventDate"
        rules={[{ required: true, message: t('editEvent.dateRequired') }]}
        extra={dateLocked ? t('simulation:openDay.pastLocked') : undefined}
      >
        <DatePicker
          format={datePickerFormat()}
          css={commonStyles.fullWidth}
          disabled={dateLocked}
          disabledDate={disableBeforeOpenDay(openDay)}
        />
      </Form.Item>
      <Form.Item label={t('editEvent.comment')} name="notes">
        <Input.TextArea rows={4} placeholder={t('editEvent.commentPlaceholder')} />
      </Form.Item>
    </Form>
  </Modal>
  );
};

export default EditEventModal;
